import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ChevronRight, ArrowRight, MapPin, Maximize, CalendarDays, Building2, CheckCircle } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useMeta } from "@/hooks/useMeta";
import { useSettings } from "@/hooks/useSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ObjektCard({ objekt }) {
  const isSold = objekt.status === "verkauft";
  const img = objekt.images?.[0];

  return (
    <Link
      to={`/objekte/${objekt.slug}`}
      className="group bg-[#0d0d0d] border border-white/5 overflow-hidden hover:border-[#E61D38]/30 transition-colors relative"
      data-testid={`objekt-card-${objekt.slug}`}
    >
      {isSold && (
        <div className="absolute top-4 right-4 z-10 bg-emerald-600 text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5" data-testid={`objekt-sold-badge-${objekt.slug}`}>
          <CheckCircle size={11} /> Erfolgreich verkauft
        </div>
      )}
      {img && (
        <div className="relative h-56 overflow-hidden">
          <img src={img} alt={objekt.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] to-transparent opacity-50" />
          {objekt.price && (
            <div className="absolute bottom-3 left-3 bg-[#0d0d0d]/80 backdrop-blur-sm text-white text-sm font-bold px-3 py-1.5 border border-white/10">
              {objekt.price}
            </div>
          )}
        </div>
      )}
      <div className="p-5">
        <h2 className="text-white font-bold text-base mb-2 group-hover:text-[#E61D38] transition-colors leading-snug">
          {objekt.title}
        </h2>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-gray-500 mb-3">
          {objekt.district && (
            <span className="flex items-center gap-1"><MapPin size={10} /> {objekt.district}</span>
          )}
          {objekt.property_type && (
            <span className="flex items-center gap-1"><Building2 size={10} /> {objekt.property_type}</span>
          )}
          {objekt.living_area && (
            <span className="flex items-center gap-1"><Maximize size={10} /> {objekt.living_area}</span>
          )}
          {objekt.year_built && (
            <span className="flex items-center gap-1"><CalendarDays size={10} /> Bj. {objekt.year_built}</span>
          )}
        </div>
        <div className="flex items-center justify-end">
          <span className="flex items-center gap-1 text-[#E61D38] text-[10px] font-bold uppercase tracking-wider">
            {isSold ? "Referenz ansehen" : "Details"} <ArrowRight size={9} className="transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function ObjektePage() {
  const settings = useSettings();

  useMeta(
    "Objekte & Referenzen | BRIMO Immobilien NRW",
    "Aktuelle Verkaufsobjekte und erfolgreich verkaufte Referenzen von BRIMO Immobilien NRW."
  );

  const [objekte, setObjekte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("alle");

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/objekte`).then((r) => {
      // Rotate order based on session - each visit gets a different start position
      const data = r.data;
      if (data.length > 1) {
        const offset = Math.floor(Math.random() * data.length);
        const rotated = [...data.slice(offset), ...data.slice(0, offset)];
        setObjekte(rotated);
      } else {
        setObjekte(data);
      }
    }).finally(() => setLoading(false));
  }, []);

  if (settings.objekte_enabled === false) return <Navigate to="/" replace />;

  const filtered = filter === "alle" ? objekte : objekte.filter((o) => o.status === filter);
  const countSale = objekte.filter((o) => o.status === "zum_verkauf").length;
  const countSold = objekte.filter((o) => o.status === "verkauft").length;

  return (
    <div className="min-h-screen bg-[#1a1a1a]" data-testid="objekte-page">
      <Navbar />

      <section className="pt-32 pb-8">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Start</Link>
            <ChevronRight size={10} />
            <span className="text-white">Objekte</span>
          </nav>
          <div className="h-0.5 w-10 bg-[#E61D38] mb-4" />
          <h1
            className="text-4xl sm:text-5xl font-black text-white uppercase tracking-tight leading-tight mb-4"
            style={{ fontFamily: "Montserrat, sans-serif" }}
            data-testid="objekte-h1"
          >
            Objekte & Referenzen
          </h1>
          <p className="text-gray-400 text-base max-w-2xl">
            Entdecken Sie unsere aktuellen Verkaufsobjekte oder lassen Sie sich von unseren erfolgreich vermittelten Referenzen inspirieren.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center gap-3 mb-8" data-testid="objekte-filter">
            {[
              { key: "alle", label: `Alle (${objekte.length})` },
              { key: "zum_verkauf", label: `Zum Verkauf (${countSale})` },
              { key: "verkauft", label: `Referenzen (${countSold})` },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`text-xs font-bold uppercase tracking-wider px-4 py-2 border transition-colors ${
                  filter === f.key
                    ? "text-[#E61D38] border-[#E61D38]/40 bg-[#E61D38]/5"
                    : "text-gray-500 border-white/10 hover:text-gray-300"
                }`}
                data-testid={`objekte-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-gray-500 text-sm">Laden...</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="objekte-empty">
              <Building2 size={32} className="text-gray-700 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Keine Objekte in dieser Kategorie.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((objekt) => (
                <ObjektCard key={objekt.id} objekt={objekt} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
